import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  AdsTemplate,
  CreateCampaignForm,
  DiySettings,
} from '../../../types/IDiy';
import { AdVideo, Brand, ChangeEventType, LeadFormData } from '../../../types';
import { Objectives } from '../../../utils/constants/facebookAds';
import { getBrandLeadForms } from '../../../services/lead';
import { AdsPreviewSquare } from './AdsPreview';
import { constantStringToHumanized } from '../../../utils/stringModifier';
import {
  buildCallToActions,
  FBDestinationType,
  FBOptimizationGoal,
} from '../../../utils/helpers/facebookAdsHelper';
import ModalHeader from '../../Modal/ModalHeader';
import Spinner from '../../Spinner';
import { securedUrlPattern } from '../../../utils/validation';

interface DiyAdPreviewV2Props {
  brand: Brand;
  diySettings: DiySettings;
  selectedTemplate: AdsTemplate;
  setSelectedTemplate: Dispatch<SetStateAction<AdsTemplate>>;
  adVideo: AdVideo;
  formValues: CreateCampaignForm;
  setFormValues: Dispatch<SetStateAction<CreateCampaignForm>>;
  isEdit: boolean;
  isAbTest: boolean;
  parsedBody: string;
  register: any;
  clearErrors: any;
  errors: any;
  setValue: any;
}

const DiyAdPreviewV2: FC<DiyAdPreviewV2Props> = ({
  brand,
  diySettings,
  selectedTemplate,
  setSelectedTemplate,
  adVideo,
  formValues,
  setFormValues,
  isEdit,
  isAbTest,
  parsedBody,
  register,
  clearErrors,
  errors,
  setValue,
}) => {
  const [leadGenForms, setLeadGenForms] = useState<LeadFormData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [defaultLeadForm, setDefaultLeadForm] = useState<LeadFormData | null>(
    null,
  );

  useEffect(() => {
    if (brand) getLeadForms();
  }, [brand, isEdit]);

  const getLeadForms = async () => {
    try {
      setLoading(true);
      const response = await getBrandLeadForms(brand?._id);

      const defaultLeadForm = response.data?.find(
        (leadForm: LeadFormData) => leadForm.default,
      );

      if (defaultLeadForm && !isEdit) {
        let temp: CreateCampaignForm = { ...formValues };

        temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
          ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
          value: {
            lead_gen_form_id: defaultLeadForm.id,
          },
        };

        setDefaultLeadForm(defaultLeadForm);
        setValue('leadGenForm', defaultLeadForm.id);
      }

      setLeadGenForms(response.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLeadAdTool = (e: ChangeEventType) => {
    let temp: CreateCampaignForm = { ...formValues };
    switch (e.target.value) {
      case 'website':
        clearErrors('leadGenForm');
        setValue('link', diySettings?.defaultUrl);
        temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
          ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
          value: { lead_gen_form_id: null, link: diySettings?.defaultUrl },
        };

        temp = {
          ...temp,
          campaignTemplate: {
            ...formValues?.campaignTemplate,
            objective: Objectives.OUTCOME_TRAFFIC,
          },
          adSetTemplate: {
            ...formValues?.adSetTemplate,
            optimization_goal: FBOptimizationGoal.LinkClicks,
            destination_type: FBDestinationType.Undefined,
          },
        };
        break;
      default:
        clearErrors('link');
        setValue('leadGenForm', defaultLeadForm?.id);
        temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
          ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
          value: { link: null, lead_gen_form_id: defaultLeadForm?.id },
        };

        temp = {
          ...temp,
          campaignTemplate: {
            ...formValues?.campaignTemplate,
            objective: Objectives.OUTCOME_LEADS,
          },
          creativeTemplate: {
            ...formValues?.creativeTemplate,
            asset_feed_spec: {
              ...formValues?.creativeTemplate?.asset_feed_spec,
            },
          },
          adSetTemplate: {
            ...formValues?.adSetTemplate,
            optimization_goal: FBOptimizationGoal.LeadGeneration,
            destination_type: FBDestinationType.OnAd,
          },
        };
        break;
    }

    temp.creativeTemplate.asset_feed_spec.call_to_action_types = [];
    temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
      ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
      type: null,
      adlabels: [{ name: 'cta_1' }],
    };

    temp = {
      ...temp,
      adTemplate: { ...temp?.adTemplate, leadAdTool: e.target.value },
    };

    setFormValues(temp);
  };

  const handleOnChangeWebsiteUrl = (e: ChangeEventType) => {
    let temp: CreateCampaignForm = { ...formValues };
    clearErrors('link');

    temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
      ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
      value: {
        link: e.target.value,
      },
    };

    temp = {
      ...temp,
      creativeTemplate: {
        ...formValues?.creativeTemplate,
        asset_feed_spec: {
          ...formValues?.creativeTemplate?.asset_feed_spec,
          link_urls: [
            {
              website_url: e.target.value,
              display_url: e.target.value,
            },
          ],
        },
      },
    };

    setFormValues(temp);
  };

  const handleSelectLeadForm = (value: LeadFormData) => {
    let temp: CreateCampaignForm = { ...formValues };
    clearErrors('leadGenForm');
    temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
      ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
      value: {
        lead_gen_form_id: value.id,
      },
    };

    setValue('leadGenForm', value.id);
    setFormValues(temp);
  };

  const handleOnSelectCTA = (value: string) => {
    let temp: CreateCampaignForm = { ...formValues };
    clearErrors('callToAction');
    temp.creativeTemplate.asset_feed_spec.call_to_action_types = [value];
    temp.creativeTemplate.asset_feed_spec.call_to_actions[0] = {
      ...temp.creativeTemplate.asset_feed_spec.call_to_actions[0],
      type: value,
      adlabels: [{ name: 'cta_1' }],
    };

    setValue('callToAction', value);
    setFormValues(temp);
  };

  const handleOnChangeCustomField = (e: ChangeEventType) => {
    const name = e.target.name;
    clearErrors(name);
    let temp: AdsTemplate = { ...selectedTemplate };
    let originalString: string = parsedBody;

    const index = temp?.customFields?.findIndex((value: any) => {
      return value.field === name;
    });

    temp.customFields[index].value = e.target.value;

    temp.customFields.forEach((custom: any) => {
      const { field, value } = custom;

      originalString = originalString.replace(field, value);
    });

    formValues.creativeTemplate.asset_feed_spec.bodies[0].text = originalString;

    setFormValues(formValues);
    setSelectedTemplate(temp);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <Grid container spacing={2}>
          {!isEdit && !isAbTest ? (
            <Grid item xs={12}>
              <FormControl>
                <Typography variant="body2">
                  What tools you want to use to gather leads?
                </Typography>

                <RadioGroup
                  row
                  aria-labelledby="leadAdTool"
                  name="leadAdTool"
                  value={formValues?.adTemplate?.leadAdTool}
                  onChange={handleSelectLeadAdTool}
                >
                  <FormControlLabel
                    value={'instant_form'}
                    control={<Radio size="small" />}
                    label="Instant Form"
                  />

                  <FormControlLabel
                    value={'website'}
                    control={<Radio size="small" />}
                    label="Website"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          ) : null}

          {formValues?.campaignTemplate?.objective ===
            Objectives.OUTCOME_LEADS &&
          (!isEdit || (isEdit && formValues?.isFormChanged)) ? (
            <>
              <Grid item xs={12}>
                <Typography variant="body1" fontWeight="bold">
                  Instant form
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption">
                  Make connections with people by letting them send contact
                  information and other details to you through a form.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  id="leadGenForm"
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {`${option.name} (${option.id})`}
                      </li>
                    );
                  }}
                  options={leadGenForms.sort(
                    (a, b) => -b.name?.localeCompare(a.name),
                  )}
                  value={
                    leadGenForms?.find(
                      (form: LeadFormData) =>
                        form.id ===
                        formValues?.creativeTemplate?.asset_feed_spec
                          ?.call_to_actions?.[0]?.value?.lead_gen_form_id,
                    ) || null
                  }
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...register('leadGenForm', { required: true })}
                      variant="standard"
                      label="Lead Gen Form"
                      fullWidth
                      size="small"
                      placeholder="Select Form Lead"
                      required
                      name="leadGenForm"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {loading ? <Spinner /> : null}

                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                      error={!!errors?.leadGenForm}
                      helperText={
                        !!errors?.leadGenForm && 'Lead Gen Form is required'
                      }
                    />
                  )}
                  disableClearable
                  onChange={(e: any, value: any) => handleSelectLeadForm(value)}
                  loading={loading}
                />
              </Grid>
            </>
          ) : null}

          {formValues?.campaignTemplate?.objective ===
            Objectives.OUTCOME_TRAFFIC &&
          (!isEdit || (isEdit && formValues?.isWebsiteUrlChanged)) ? (
            <Grid item xs={12}>
              <TextField
                {...register('link', {
                  required: true,
                  pattern: securedUrlPattern,
                })}
                fullWidth
                required
                size="small"
                value={
                  formValues?.creativeTemplate?.asset_feed_spec
                    ?.call_to_actions?.[0]?.value?.link
                }
                name="link"
                label="Website URL"
                onChange={handleOnChangeWebsiteUrl}
                variant="standard"
                InputLabelProps={{ shrink: true }}
                error={!!errors?.link}
                helperText={
                  errors?.link?.message ||
                  (!!errors?.link && 'Website link is required')
                }
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Autocomplete
              id="callToAction"
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option}>
                    {constantStringToHumanized(option)}
                  </li>
                );
              }}
              options={buildCallToActions(
                formValues?.campaignTemplate?.objective,
              )?.sort((a, b) => -b.localeCompare(a))}
              value={
                formValues?.creativeTemplate?.asset_feed_spec
                  ?.call_to_action_types[0] || null
              }
              getOptionLabel={(option) => constantStringToHumanized(option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...register('callToAction', { required: true })}
                  variant="standard"
                  label="Call-to-Action"
                  fullWidth
                  name="callToAction"
                  size="small"
                  placeholder="Select a call to action"
                  required
                  InputLabelProps={{ shrink: true }}
                  error={!!errors?.callToAction}
                  helperText={
                    errors?.callToAction && 'Call-to-Action is required'
                  }
                />
              )}
              disableClearable
              onChange={(e: any, value: any) => handleOnSelectCTA(value)}
            />
          </Grid>

          {selectedTemplate?.customFields?.length > 0 ? (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" fontWeight="bold">
                    Custom Fields
                  </Typography>
                </Grid>

                {selectedTemplate?.customFields?.map(
                  (custom: any, index: number) => {
                    return (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            {...register(custom?.field, { required: true })}
                            fullWidth
                            required
                            variant="standard"
                            type="text"
                            id={`value-${index + 1}`}
                            name={custom.field}
                            label={custom?.field}
                            onChange={handleOnChangeCustomField}
                            value={custom?.value}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            error={!!errors?.[custom?.field]}
                            helperText={
                              !!errors?.[custom?.field] &&
                              `${custom?.field} is required`
                            }
                          />
                        </Grid>
                      </>
                    );
                  },
                )}
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </Grid>

      <Grid item xs={12} sm={8}>
        <Grid container spacing={2} sx={{ position: 'sticky', top: 0 }}>
          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <ModalHeader title="Ad Preview" />
          </Grid>

          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <AdsPreviewSquare
              page={diySettings?.facebookPage}
              body={
                formValues?.creativeTemplate?.asset_feed_spec?.bodies[0]?.text
              }
              isImage={selectedTemplate?.media === 'image'}
              imageUrl={selectedTemplate?.images?.square}
              websiteUrl={
                formValues?.creativeTemplate?.asset_feed_spec?.link_urls[0]
                  ?.website_url
              }
              headline={
                formValues?.creativeTemplate?.asset_feed_spec?.titles[0]?.text
              }
              description={
                formValues?.creativeTemplate?.asset_feed_spec?.descriptions[0]
                  ?.text
              }
              cta={
                formValues?.creativeTemplate?.asset_feed_spec
                  ?.call_to_action_types[0]
              }
              videoUrl={adVideo?.source}
              objective={formValues?.campaignTemplate?.objective}
              brand={brand}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiyAdPreviewV2;
